'use client';

/**
 * Advanced Home · "Connect your agents" card.
 *
 * Surfaces the api-gateway's MCP / API / SDK coordinates (env-configured,
 * fetched via useConnectionInfo) on the dashboard home page, mirroring the
 * Simple Mode connect surface. "Full connect guide" flips into Simple Mode.
 */

import { useState } from 'react';
import { Server, Radio, Terminal, Package, Copy, Check, ArrowRight } from 'lucide-react';
import { useConnectionInfo } from '@/lib/hooks/useConnectionInfo';
import { useUiStore } from '@/lib/stores/ui-store';
import { useScopeStore } from '@/lib/stores/scope-store';

function projectScoped(baseUrl: string, projectId: string | undefined): string {
  if (!projectId) return baseUrl;
  if (baseUrl.includes('{projectId}')) return baseUrl.replace('{projectId}', projectId);
  return `${baseUrl.replace(/\/$/, '')}/${projectId}`;
}

function CopyRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  const [ok, setOk] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard?.writeText(value);
    } catch {
      /* clipboard unavailable — non-fatal */
    }
    setOk(true);
    setTimeout(() => setOk(false), 1400);
  };
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <Icon className="w-4 h-4 text-cs-text-muted flex-shrink-0" aria-hidden="true" />
      <span className="w-24 flex-shrink-0 text-xs text-cs-text-secondary">{label}</span>
      <code className="flex-1 truncate font-mono text-[12px] text-cs-text-primary">{value}</code>
      <button
        type="button"
        onClick={copy}
        aria-label={ok ? 'Copied' : `Copy ${label}`}
        className="inline-flex items-center gap-1 rounded-md border border-cs-border px-2 py-1 text-[11px] text-cs-text-secondary hover:bg-cs-surface-hover transition-colors"
      >
        {ok ? <Check className="w-3 h-3 text-cs-success-400" aria-hidden="true" /> : <Copy className="w-3 h-3" aria-hidden="true" />}
        {ok ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export function ConnectAgentsCard() {
  const { connection } = useConnectionInfo();
  const setMode = useUiStore((s) => s.setMode);
  const projectId = useScopeStore((s) => s.project?.id);
  const mcpServerUrl = projectScoped(connection.mcpServerUrl, projectId);
  const mcpSseUrl = projectScoped(connection.mcpSseUrl, projectId);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold text-cs-text-muted uppercase tracking-wide">Connect your agents</h2>
        <button
          type="button"
          onClick={() => setMode('simple')}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-cs-primary-400 hover:text-cs-primary-300"
        >
          Full connect guide <ArrowRight className="w-3 h-3" aria-hidden="true" />
        </button>
      </div>
      <div className="bg-cs-surface-card border border-cs-border rounded-lg divide-y divide-cs-border">
        <CopyRow icon={Server} label="MCP (HTTP)" value={mcpServerUrl} />
        <CopyRow icon={Radio} label="MCP (SSE)" value={mcpSseUrl} />
        <CopyRow icon={Terminal} label="API base" value={connection.apiBaseUrl} />
        <CopyRow icon={Package} label="Python SDK" value={connection.sdk.python.install} />
        {connection.sdk.node?.install && (
          <CopyRow icon={Package} label="Node SDK" value={connection.sdk.node.install} />
        )}
      </div>
      <p className="text-[11px] text-cs-text-muted">
        Same self-organizing context over MCP, REST or SDK — configured in the api-gateway and served live.
      </p>
    </div>
  );
}
